import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, Observable } from 'rxjs';
import { CurrentMemberProvider } from '../../auth/services/current-member-provider.service';
import { Survey } from '../../satisfaction/models/survey.model';
import { SurveyService } from '../../satisfaction/services/survey.service';
import { SatisfactionPopupComponent } from '../components/satisfaction-popup/satisfaction-popup.component';
import { RoomBooking } from '../models/room-booking/room-booking.model';
import { CourseInstance } from '../models/training/course-instance.model';
import { SurveyTopicService } from './survey-topic.service';

@Injectable({
  providedIn: 'root',
})
export class SatisfactionPopupService {

  dialogRef: MatDialogRef<SatisfactionPopupComponent>;

  constructor(
    private surveyTopicService: SurveyTopicService,
    private surveyService: SurveyService,
    private toastr: ToastrService,
    private dialog: MatDialog,
    private currentMemberProvider: CurrentMemberProvider,
  ) {
  }

  openPopupIfApplicable(): void {
    if (!this.currentMemberProvider.isAuthenticated()) {
      return;
    }

    this.getPopupData().subscribe(
      ([surveys, topics]) => {
        const popupTopic = this.getPopupSurveyTopic(topics, surveys);
        if (popupTopic) {
          const popupSurvey: Survey = this.getRelatedSurvey(popupTopic, surveys);
          this.openModal(popupTopic, popupSurvey);
        }
      },
      (error) => this.onError(error),
    );
  }

  /**
   * Returns all data required to allow or not a survey popup display
   */
  getPopupData(): Observable<any> {
    return forkJoin([
      this.surveyService.getAll(),
      this.surveyTopicService.getNonAnsweredSurveyTopics(),
    ]);
  }

  /**
   * Opens the satisfaction modal window based on survey topic and surveys
   *
   * @param surveyTopic Subject of the survey
   * @param survey Survey related to the survey topic
   */
  openModal(surveyTopic: RoomBooking | CourseInstance, survey: Survey): void {
    this.dialogRef = this.dialog.open(SatisfactionPopupComponent, {
      data: {
        object: surveyTopic,
        survey,
        text: 'text',
        textReplaces: [''],
        buttonRoute: '',
      },
      minWidth: document.documentElement.offsetWidth / 2 + 'px',
      minHeight: document.documentElement.offsetHeight / 2 + 'px',
      hasBackdrop: true,
      closeOnNavigation: true,
      panelClass: 'resultsPanel',
    });

    this.dialogRef.afterClosed().subscribe(() => this.dialogRef = undefined);
  }

  /**
   * Returns whether there is a survey that is enabled and applicable for the survey topic
   *
   * @param surveyTopic Survey topic
   * @param surveys Array of surveys currently available
   */
  private isSurveyRelatedToTopic(surveyTopic: RoomBooking | CourseInstance, surveys: Survey[]): boolean {
    return surveys.some(survey => surveyTopic['@id'].includes(survey.topic));
  }

  /**
   * Returns the survey topic that should be displayed in popup
   *
   * @param surveyTopics All non answered survey topics
   * @param surveys Currently available surveys
   */
  private getPopupSurveyTopic(
    surveyTopics: any[],
    surveys: Survey[],
  ): RoomBooking | CourseInstance | null {
    if (surveyTopics && surveyTopics.length > 0) {
      return surveyTopics.find(topic => this.isSurveyRelatedToTopic(topic, surveys));
    }

    return null;
  }

  /**
   * Displays toastr on error
   *
   * @param error Error to display
   */
  private onError(error): void {
    this.toastr.error(error);
  }

  /**
   * Returns survey topic related survey
   *
   * @param topic Topic you want the survey of
   * @param surveys Array of available surveys
   */
  private getRelatedSurvey(topic: RoomBooking | CourseInstance, surveys: Survey[]): Survey {
    return surveys.find(survey => this.isSurveyRelatedToTopic(topic, [survey]));
  }
}
